import { Component } from './component.js';
import {
  KEYCAP_COLORS,
  BASE_COLORS,
  SWITCH_TYPES,
  GRID_LAYOUTS,
  COLOR_TEMPLATES,
  getKeycapsByFamily,
  getBaseColorsByFamily,
  getTemplate
} from './fidget-colors.js';

/**
 * FidgetCustomizer Component
 * Handles all interactive customization for fidget toys
 */
class FidgetCustomizer extends Component {
  constructor() {
    super();
    
    // Get configuration from element
    this.productSize = this.getAttribute('data-product-size') || '9-key';
    this.productId = this.getAttribute('data-product-id');
    this.formId = this.getAttribute('data-form-id');
    
    // Get grid layout
    this.layout = /** @type {Record<string, any>} */ (GRID_LAYOUTS)[this.productSize];
    
    // Initialize state
    /** @type {any} */
    this.state = {
      baseColor: null,
      switches: /** @type {Record<number, string>} */ ({}),
      keycaps: /** @type {Record<number, any>} */ ({}),
      designMode: 'all-same',
      selectedTemplate: /** @type {string | null} */ (null),
      switchMode: 'all-clicky',
      customSwitchMode: false,
      allSameColor: null,
      specialInstructions: '',
      showAllBaseColors: false,
      showAllKeycapColors: false,
      currentBaseColorFilter: 'all',
      currentBaseFinishFilter: 'all',
      currentPaletteFilter: 'all',
      currentTemplateCategory: 'pride'
    };
    
    // Initialize switches to all clicky
    this.layout.positions.forEach(/** @param {number} pos */ (pos) => {
      this.state.switches[pos] = 'clicky';
    });
    
    // Initialize keycaps to null
    this.layout.positions.forEach(/** @param {number} pos */ (pos) => {
      this.state.keycaps[pos] = null;
    });
  }
  
  /**
   * Called when component is connected to DOM
   */
  connectedCallback() {
    super.connectedCallback();
    
    console.log('🎯 Fidget Customizer connected:', {
      productSize: this.productSize,
      productId: this.productId,
      formId: this.formId
    });
    
    // Skip initialization if we don't have required data (likely a ghost instance)
    if (!this.productId || !this.formId) {
      console.warn('⚠️ Skipping initialization: Missing productId or formId (probably a template preview)');
      return;
    }
    
    console.log('📝 Hidden input refs:', {
      inputBaseColor: !!this.refs.inputBaseColor,
      inputSwitchConfig: !!this.refs.inputSwitchConfig,
      inputKeycapConfig: !!this.refs.inputKeycapConfig,
      inputSpecialInstructions: !!this.refs.inputSpecialInstructions,
      inputConfigJSON: !!this.refs.inputConfigJSON,
      inputOrderID: !!this.refs.inputOrderID
    });
    
    // Move hidden inputs into the form and store references to the form inputs
    this.setupFormInputs();
    
    // Render all UI elements
    this.renderBaseColorGrid();
    this.renderSwitchGrid();
    this.renderKeycapGrid();
    this.renderTemplateGrid();
    this.renderAllSameColorGrid();
    this.renderColorPalette();
    
    // Set initial design mode
    this.handleDesignModeChange();
    
    // Add event listeners for text inputs
    if (this.refs.specialInstructions) {
      /** @type {HTMLElement} */ (this.refs.specialInstructions).addEventListener('input', () => {
        this.updateFormInputs();
      });
    }
    
    // Add form submit listener to generate fresh unique ID
    this.addFormSubmitListener();
    
    // Update summary
    this.updateSummary();
    
    // Initialize form inputs with current state
    this.updateFormInputs();
    
    // Update add to cart button state (with small delay to ensure button is in DOM)
    setTimeout(() => this.updateAddToCartButton(), 100);
    // Also check after a longer delay in case of slow loading
    setTimeout(() => this.updateAddToCartButton(), 500);
  }
  
  /**
   * Add listener to form submission to regenerate unique ID and inject properties
   */
  addFormSubmitListener() {
    if (!this.formId) {
      console.error('❌ Cannot add form submit listener: No form ID');
      return;
    }
    
    const form = document.getElementById(this.formId);
    if (!form) {
      console.error(`❌ Fidget Customizer: Form with ID "${this.formId}" not found for submit listener`);
      return;
    }
    
    console.log(`✅ Added submit listener to form: ${this.formId}`);
    
    // Listen for form submission (before the form's natural submit)
    form.addEventListener('submit', (e) => {
      console.log('📦 Form submit event fired');
      
      // Generate a fresh unique ID right before submission
      this.generateUniqueOrderID();
      
      // Update all form inputs to ensure they're current
      this.updateFormInputs();
      
      // Verify form inputs have correct values
      this.verifyFormInputs();
      
      // Log for debugging
      const inputBaseColor = this.formInputs?.baseColor?.value || 'missing';
      const inputSwitchConfig = this.formInputs?.switchConfig?.value || 'missing';
      const inputKeycapConfig = this.formInputs?.keycapConfig?.value || 'missing';
      const inputOrderID = this.formInputs?.orderID?.value || 'missing';
      
      console.log('🛒 Form submitting with properties:', {
        'Base Color': inputBaseColor,
        'Switch Configuration': inputSwitchConfig,
        'Keycap Configuration': inputKeycapConfig,
        '_order_id': inputOrderID
      });
      
      // Warn if any property is missing
      if (inputBaseColor === 'missing' || inputSwitchConfig === 'missing' || inputKeycapConfig === 'missing') {
        console.error('❌ ERROR: Some properties are missing!');
        console.error('Form inputs:', this.formInputs);
      }
      
      // Warn if keycap config looks wrong
      if (inputKeycapConfig.includes('Please contact us')) {
        console.error('⚠️ WARNING: Keycap configuration contains error message!');
        console.error('State:', {
          designMode: this.state.designMode,
          allSameColor: this.state.allSameColor,
          keycaps: this.state.keycaps
        });
      }
    }, { capture: true }); // Use capture phase to run before other handlers
  }
  
  /**
   * Verify all form inputs have current values before submission
   * This is called right before form submission to ensure everything is up to date
   */
  verifyFormInputs() {
    console.log('🔍 Verifying form inputs before submission...');
    
    if (!this.formInputs) {
      console.error('❌ Form inputs not initialized!');
      return;
    }
    
    // Log all current values
    Object.entries(this.formInputs).forEach(([key, input]) => {
      console.log(`  ${key}: ${input.value.substring(0, 50)}${input.value.length > 50 ? '...' : ''}`);
    });
  }
  
  /**
   * Generate a unique order ID to prevent cart item combining
   */
  generateUniqueOrderID() {
    if (this.formInputs && this.formInputs.orderID) {
      const uniqueID = `CFG-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      this.formInputs.orderID.value = uniqueID;
      console.log('Generated unique order ID:', uniqueID);
    }
  }
  
  /**
   * Move hidden inputs from the component into the form
   * This ensures they're always submitted with the product form
   */
  setupFormInputs() {
    if (!this.formId) {
      console.error('❌ Fidget Customizer: No form ID specified.');
      return;
    }
    
    const form = document.getElementById(this.formId);
    if (!form) {
      console.error(`❌ Fidget Customizer: Form with ID "${this.formId}" not found`);
      console.log('Available forms:', Array.from(document.querySelectorAll('form')).map(f => ({id: f.id, action: f.action})));
      return;
    }
    
    console.log(`✅ Moving hidden inputs into form: ${this.formId}`);
    
    // Initialize form inputs storage
    /** @type {Record<string, HTMLInputElement>} */
    const formInputs = {};
    
    const inputMapping = [
      { name: 'properties[Base Color]', ref: this.refs.inputBaseColor, key: 'baseColor' },
      { name: 'properties[Switch Configuration]', ref: this.refs.inputSwitchConfig, key: 'switchConfig' },
      { name: 'properties[Keycap Configuration]', ref: this.refs.inputKeycapConfig, key: 'keycapConfig' },
      { name: 'properties[Special Instructions]', ref: this.refs.inputSpecialInstructions, key: 'specialInstructions' },
      { name: 'properties[_config_json]', ref: this.refs.inputConfigJSON, key: 'configJSON' },
      { name: 'properties[_order_id]', ref: this.refs.inputOrderID, key: 'orderID' }
    ];
    
    inputMapping.forEach(({ name, ref, key }) => {
      if (ref && ref instanceof HTMLInputElement) {
        // Remove from component
        if (ref.parentNode === this) {
          this.removeChild(ref);
        }
        
        // Set attributes
        ref.type = 'hidden';
        ref.name = name;
        ref.removeAttribute('form'); // Remove form attribute since it's now in the form
        
        // Add to form
        form.appendChild(ref);
        
        // Store reference
        formInputs[key] = ref;
        
        console.log(`📌 Moved ${name} into form`);
      }
    });
    
    // Store the form inputs object
    this.formInputs = formInputs;
    
    console.log(`✅ Moved ${Object.keys(formInputs).length} hidden inputs into form`);
  }
  
  /**
   * Check if all required customizations are complete
   * @returns {boolean} True if ready to add to cart
   */
  isCustomizationComplete() {
    // Check base color
    if (!this.state.baseColor) return false;
    
    // Check keycap design
    switch (this.state.designMode) {
      case 'template':
        if (!this.state.selectedTemplate) return false;
        break;
      case 'custom':
        // All positions must have a keycap assigned
        const allFilled = this.layout.positions.every(/** @param {number} pos */ (pos) => this.state.keycaps[pos]);
        if (!allFilled) return false;
        break;
      case 'all-same':
        // Check if allSameColor is set OR if all keycaps are the same color
        if (!this.state.allSameColor) {
          const firstKeycap = this.state.keycaps[this.layout.positions[0]];
          if (!firstKeycap) return false;
          
          // Verify all positions have a keycap with the same color
          const allSameColor = this.layout.positions.every(/** @param {number} pos */ (pos) => {
            const keycap = this.state.keycaps[pos];
            return keycap && keycap.coordinate === firstKeycap.coordinate;
          });
          
          if (!allSameColor) return false;
        }
        break;
      case 'designer-choice':
        // Always valid once selected
        break;
    }
    
    return true;
  }
  
  /**
   * Update the add to cart button state based on customization completion
   */
  updateAddToCartButton() {
    const isComplete = this.isCustomizationComplete();
    
    // Find all add to cart buttons (main, sticky, etc.)
    /** @type {HTMLButtonElement[]} */
    const buttons = [];
    
    // 1. Main Add to Cart button (in add-to-cart-component)
    const mainButton = document.querySelector('add-to-cart-component button[name="add"]') ||
                       document.querySelector('button[name="add"][type="submit"]') ||
                       document.querySelector('button[type="submit"].add-to-cart-button');
    if (mainButton) buttons.push(/** @type {HTMLButtonElement} */ (mainButton));
    
    // 2. Sticky Add to Cart button
    const stickyButton = document.querySelector('sticky-add-to-cart button.sticky-add-to-cart__button') ||
                         document.querySelector('.sticky-add-to-cart__button');
    if (stickyButton) buttons.push(/** @type {HTMLButtonElement} */ (stickyButton));
    
    // 3. Any other add-to-cart buttons
    const otherButtons = document.querySelectorAll('button.add-to-cart-button:not([name="add"])');
    otherButtons.forEach(/** @param {Element} btn */ (btn) => {
      const htmlBtn = /** @type {HTMLButtonElement} */ (btn);
      if (!buttons.includes(htmlBtn)) buttons.push(htmlBtn);
    });
    
    // Update all button states
    buttons.forEach(button => {
      if (isComplete) {
        /** @type {HTMLButtonElement} */ (button).disabled = false;
        button.removeAttribute('aria-disabled');
      } else {
        /** @type {HTMLButtonElement} */ (button).disabled = true;
        button.setAttribute('aria-disabled', 'true');
      }
    });
    
    // 4. Handle accelerated checkout (Shop Pay, PayPal, etc.)
    const acceleratedCheckout = document.querySelector('.accelerated-checkout-block') ||
                                document.querySelector('[data-shopify="payment-button"]') ||
                                document.querySelector('shopify-accelerated-checkout');
    
    if (acceleratedCheckout) {
      if (isComplete) {
        // Show and enable accelerated checkout
        /** @type {HTMLElement} */ (acceleratedCheckout).style.display = '';
        /** @type {HTMLElement} */ (acceleratedCheckout).style.pointerEvents = '';
        /** @type {HTMLElement} */ (acceleratedCheckout).style.opacity = '';
        acceleratedCheckout.removeAttribute('aria-hidden');
      } else {
        // Hide and disable accelerated checkout
        /** @type {HTMLElement} */ (acceleratedCheckout).style.display = 'none';
        /** @type {HTMLElement} */ (acceleratedCheckout).style.pointerEvents = 'none';
        /** @type {HTMLElement} */ (acceleratedCheckout).style.opacity = '0.5';
        acceleratedCheckout.setAttribute('aria-hidden', 'true');
      }
    }
  }
  
  /**
   * Render base color selection grid
   */
  renderBaseColorGrid() {
    const grid = /** @type {HTMLElement} */ (this.refs.baseColorGrid);
    if (!grid) return;
    
    grid.innerHTML = ''; // Clear existing swatches
    
    const colors = Object.entries(BASE_COLORS);
    const initialCount = 16; // Show first 16 colors initially
    let visibleIndex = 0;
    
    // Store currently selected color key to re-apply after render
    const selectedKey = this.state.baseColor?.key;
    
    colors.forEach(([key, color]) => {
      // Filter by family if needed
      if (this.state.currentBaseColorFilter !== 'all') {
        // Support both single family (string) and multiple families (array)
        const families = Array.isArray(color.family) ? color.family : [color.family];
        if (!families.includes(this.state.currentBaseColorFilter)) {
          return; // Skip colors not in the selected family
        }
      }
      
      // Filter by finish if needed
      if (this.state.currentBaseFinishFilter !== 'all') {
        if (color.finish !== this.state.currentBaseFinishFilter) {
          return; // Skip colors not matching the selected finish
        }
      }
      
      const swatch = document.createElement('div');
      swatch.className = 'color-swatch';
      swatch.style.backgroundColor = color.hex;
      swatch.dataset.colorKey = key;
      
      // Re-apply selected state
      if (selectedKey && key === selectedKey) {
        swatch.classList.add('selected');
      }
      
      // Handle unavailable colors
      const isAvailable = color.available !== false;
      if (!isAvailable) {
        swatch.classList.add('color-swatch--unavailable');
        swatch.setAttribute('aria-disabled', 'true');
        swatch.style.cursor = 'not-allowed';
      }
      
      if (visibleIndex >= initialCount && !this.state.showAllBaseColors) {
        swatch.style.display = 'none';
        swatch.dataset.hidden = 'true';
      }
      
      const label = document.createElement('div');
      label.className = 'color-swatch__label';
      // Add finish type to label
      const finishLabel = color.finish && color.finish !== 'satin' ? ` (${color.finish.charAt(0).toUpperCase() + color.finish.slice(1)})` : '';
      label.textContent = color.name + finishLabel;
      swatch.appendChild(label);
      
      // Add finish badge for specialty finishes
      if (color.finish === 'silk' || color.finish === 'metallic') {
        const finishBadge = document.createElement('div');
        finishBadge.className = 'color-swatch__finish-badge';
        finishBadge.textContent = color.finish === 'silk' ? '✨' : '💫';
        finishBadge.title = color.finish === 'silk' ? 'Silk Finish' : 'Metallic Finish';
        swatch.appendChild(finishBadge);
      }
      
      // Add out of stock badge
      if (!isAvailable) {
        const badge = document.createElement('div');
        badge.className = 'color-swatch__badge';
        badge.textContent = 'Out of Stock';
        swatch.appendChild(badge);
      }
      
      // Only allow clicking on available colors
      if (isAvailable) {
        swatch.addEventListener('click', () => this.selectBaseColor(key, color));
      }
      
      grid.appendChild(swatch);
      visibleIndex++;
    });
  }
  
  /**
   * Select base color
   * @param {string} key - The color key
   * @param {any} color - The color object with name, hex, and category
   */
  selectBaseColor(key, color) {
    this.state.baseColor = /** @type {any} */ ({ key, ...color });
    
    // Update UI
    const swatches = /** @type {HTMLElement} */ (this.refs.baseColorGrid).querySelectorAll('.color-swatch');
    swatches.forEach(/** @param {HTMLElement} s */ (s) => {
      if (s.dataset.colorKey === key) {
        s.classList.add('selected');
      } else {
        s.classList.remove('selected');
      }
    });
    
    // Update display
    /** @type {HTMLElement} */ (this.refs.baseColorName).textContent = color.name;
    
    // Update summary and form
    this.updateSummary();
    this.updateFormInputs();
    this.updateAddToCartButton();
  }
  
  /**
   * Toggle showing all base colors
   */
  toggleAllBaseColors() {
    this.state.showAllBaseColors = !this.state.showAllBaseColors;
    
    const grid = /** @type {HTMLElement} */ (this.refs.baseColorGrid);
    if (grid) {
      // Toggle expanded class for height transition
      if (this.state.showAllBaseColors) {
        grid.classList.add('expanded');
      } else {
        grid.classList.remove('expanded');
      }
    }
    
    this.renderBaseColorGrid(); // Re-render to apply the show/hide logic
    
    // Update button text
    if (this.refs.baseColorToggleText) {
      /** @type {HTMLElement} */ (this.refs.baseColorToggleText).textContent = 
        this.state.showAllBaseColors ? 'Show Less' : 'Show All Colors';
    }
  }
  
  /**
   * Filter base colors by family
   * @param {any} event - The click event
   */
  filterBaseColors(event) {
    const family = event.target.dataset.family;
    if (!family) return;
    
    this.state.currentBaseColorFilter = family;
    
    // Show all colors when filtering (except for 'all' which shows limited by default)
    if (family === 'all') {
      this.state.showAllBaseColors = false; // Reset to showing limited for 'all'
    } else {
      this.state.showAllBaseColors = true; // Show all matching colors when filtering
    }
    
    // Update button states
    const buttons = this.querySelectorAll('.base-color-filter-button');
    buttons.forEach(/** @param {Element} btn */ (btn) => {
      if (/** @type {HTMLElement} */ (btn).dataset.family === family) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });
    
    // Re-render grid with filter applied
    this.renderBaseColorGrid();
    
    // Update toggle button text based on state
    if (this.refs.baseColorToggleText) {
      /** @type {HTMLElement} */ (this.refs.baseColorToggleText).textContent = 
        this.state.showAllBaseColors ? 'Show Less' : 'Show All Colors';
    }
  }
  
  /**
   * Filter base colors by finish type
   * @param {any} event - The click event
   */
  filterBaseByFinish(event) {
    const finish = event.target.dataset.finish;
    if (!finish) return;
    
    this.state.currentBaseFinishFilter = finish;
    
    // Show all colors when filtering by finish (except for 'all')
    if (finish === 'all') {
      this.state.showAllBaseColors = false;
    } else {
      this.state.showAllBaseColors = true;
    }
    
    // Update button states
    const buttons = this.querySelectorAll('.base-finish-filter-button');
    buttons.forEach(/** @param {Element} btn */ (btn) => {
      if (/** @type {HTMLElement} */ (btn).dataset.finish === finish) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });
    
    // Re-render grid with filter applied
    this.renderBaseColorGrid();
    
    // Update toggle button text based on state
    if (this.refs.baseColorToggleText) {
      /** @type {HTMLElement} */ (this.refs.baseColorToggleText).textContent = 
        this.state.showAllBaseColors ? 'Show Less' : 'Show All Colors';
    }
  }
  
  /**
   * Render switch configuration grid
   */
  renderSwitchGrid() {
    const grid = /** @type {HTMLElement} */ (this.refs.switchGrid);
    if (!grid) return;
    
    grid.innerHTML = '';
    grid.className = `fidget-grid fidget-grid--switches grid-${this.productSize}`;
    
    this.layout.positions.forEach(/** @param {number} position */ (position) => {
      const cell = document.createElement('div');
      cell.className = 'fidget-grid__cell';
      cell.dataset.position = String(position);
      
      const number = document.createElement('div');
      number.className = 'fidget-grid__cell-number';
      number.textContent = String(position);
      
      const icon = document.createElement('div');
      icon.className = 'fidget-grid__cell-icon';
      icon.dataset.ref = `switchIcon-${position}`;
      
      const label = document.createElement('div');
      label.className = 'fidget-grid__cell-label';
      label.dataset.ref = `switchLabel-${position}`;
      
      cell.appendChild(number);
      cell.appendChild(icon);
      cell.appendChild(label);
      
      cell.addEventListener('click', () => this.cycleSwitchType(position));
      
      grid.appendChild(cell);
    });
    
    // Initial update
    this.updateSwitchGridDisplay();
  }
  
  /**
   * Update switch grid display
   */
  updateSwitchGridDisplay() {
    this.layout.positions.forEach(/** @param {number} position */ (position) => {
      const switchType = this.state.switches[position];
      if (!switchType) return;
      const switchData = /** @type {Record<string, any>} */ (SWITCH_TYPES)[switchType];
      
      const switchGrid = /** @type {HTMLElement | null} */ (this.refs.switchGrid);
      if (!switchGrid) return;
      const cell = switchGrid.querySelector(`[data-position="${position}"]`);
      if (!cell) return;
      
      const icon = cell.querySelector('.fidget-grid__cell-icon');
      const label = cell.querySelector('.fidget-grid__cell-label');
      
      if (icon) icon.textContent = switchData.icon;
      if (label) label.textContent = switchData.name;
    });
  }
  
  /**
   * Cycle through switch types for a position
   * @param {number} position - The position to cycle
   */
  cycleSwitchType(position) {
    const types = Object.keys(SWITCH_TYPES);
    if (types.length === 0) return;
    const currentType = this.state.switches[position] || types[0] || 'clicky';
    const currentIndex = types.indexOf(currentType);
    const nextIndex = (currentIndex + 1) % types.length;
    const nextType = types[nextIndex];
    if (!nextType) return;
    
    this.state.switches[position] = nextType;
    this.state.switchMode = 'custom';
    this.state.customSwitchMode = true;
    
    this.updateSwitchGridDisplay();
    this.updateSwitchPresetButtons();
    this.updateSummary();
    this.updateFormInputs();
    this.updateAddToCartButton();
  }
  
  /**
   * Apply switch preset
   * @param {any} event - The click event
   */
  applySwitchPreset(event) {
    const preset = event.target.dataset.preset;
    if (!preset) return;
    
    const switchType = preset.replace('all-', '');
    
    this.layout.positions.forEach(/** @param {number} position */ (position) => {
      this.state.switches[position] = switchType;
    });
    
    this.state.switchMode = preset;
    this.state.customSwitchMode = false;
    
    // Hide custom grid
    if (this.refs.switchGridContainer) {
      /** @type {HTMLElement} */ (this.refs.switchGridContainer).style.display = 'none';
    }
    
    this.updateSwitchGridDisplay();
    this.updateSwitchPresetButtons();
    this.updateSummary();
    this.updateFormInputs();
    this.updateAddToCartButton();
  }
  
  /**
   * Toggle custom switch mode
   */
  toggleCustomSwitchMode() {
    this.state.customSwitchMode = !this.state.customSwitchMode;
    
    if (this.refs.switchGridContainer) {
      /** @type {HTMLElement} */ (this.refs.switchGridContainer).style.display = 
        this.state.customSwitchMode ? 'block' : 'none';
    }
    
    if (this.state.customSwitchMode) {
      this.state.switchMode = 'custom';
    }
    
    this.updateSwitchPresetButtons();
  }
  
  /**
   * Update switch preset button states
   */
  updateSwitchPresetButtons() {
    const buttons = this.querySelectorAll('.preset-button');
    buttons.forEach(/** @param {Element} btn */ (btn) => {
      const preset = /** @type {HTMLElement} */ (btn).dataset.preset;
      if (preset && preset === this.state.switchMode) {
        btn.classList.add('active');
      } else if (btn === this.refs.customSwitchToggle && this.state.customSwitchMode) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });
  }
  
  /**
   * Render keycap configuration grid
   */
  renderKeycapGrid() {
    const grid = /** @type {HTMLElement} */ (this.refs.keycapGrid);
    if (!grid) return;
    
    grid.innerHTML = '';
    grid.className = `fidget-grid fidget-grid--keycaps grid-${this.productSize}`;
    
    this.layout.positions.forEach(/** @param {number} position */ (position) => {
      const cell = document.createElement('div');
      cell.className = 'fidget-grid__cell';
      cell.dataset.position = String(position);
      cell.style.backgroundColor = '#e0e0e0';
      
      const number = document.createElement('div');
      number.className = 'fidget-grid__cell-number';
      number.textContent = String(position);
      
      const label = document.createElement('div');
      label.className = 'fidget-grid__cell-label';
      label.textContent = '—';
      label.style.fontSize = '0.625rem';
      
      cell.appendChild(number);
      cell.appendChild(label);
      
      cell.addEventListener('click', () => this.selectKeycapPosition(position));
      
      grid.appendChild(cell);
    });
  }
  
  /**
   * Select a keycap position (for custom design mode)
   * @param {number} position - The position to select
   */
  selectKeycapPosition(position) {
    // Highlight selected position
    const cells = /** @type {HTMLElement} */ (this.refs.keycapGrid).querySelectorAll('.fidget-grid__cell');
    cells.forEach(/** @param {HTMLElement} cell */ (cell) => {
      if (cell.dataset.position === position.toString()) {
        cell.style.borderColor = 'var(--color-button-primary-background, #000)';
        cell.style.borderWidth = '4px';
      } else {
        cell.style.borderColor = 'rgba(0, 0, 0, 0.2)';
        cell.style.borderWidth = '3px';
      }
    });
    
    // Store selected position for next color click
    this.selectedKeycapPosition = position;
  }
  
  /**
   * Apply color to currently selected keycap position
   * @param {string} coordinate - The color coordinate
   */
  applyColorToKeycap(coordinate) {
    if (!this.selectedKeycapPosition) {
      // If no position selected, select first empty position
      const firstEmpty = this.layout.positions.find(/** @param {number} pos */ (pos) => !this.state.keycaps[pos]);
      this.selectedKeycapPosition = firstEmpty || this.layout.positions[0];
    }
    
    const position = this.selectedKeycapPosition;
    const color = /** @type {Record<string, any>} */ (KEYCAP_COLORS)[coordinate];
    
    if (!color) return;
    
    this.state.keycaps[position] = { coordinate, ...color };
    
    // Update grid display
    this.updateKeycapGridDisplay();
    
    // Auto-advance to next position
    const currentIndex = this.layout.positions.indexOf(position);
    const nextIndex = (currentIndex + 1) % this.layout.positions.length;
    this.selectKeycapPosition(this.layout.positions[nextIndex]);
    
    this.updateSummary();
    this.updateFormInputs();
    this.updateAddToCartButton();
  }
  
  /**
   * Update keycap grid display
   */
  updateKeycapGridDisplay() {
    this.layout.positions.forEach(/** @param {number} position */ (position) => {
      const keycap = this.state.keycaps[position];
      const cell = /** @type {HTMLElement} */ (this.refs.keycapGrid)?.querySelector(`[data-position="${position}"]`);
      
      if (!cell) return;
      
      if (keycap) {
        /** @type {HTMLElement} */ (cell).style.backgroundColor = keycap.hex;
        const label = cell.querySelector('.fidget-grid__cell-label');
        if (label) {
          /** @type {HTMLElement} */ (label).textContent = keycap.coordinate;
          // Adjust text color based on background
          const rgb = this.hexToRgb(keycap.hex);
          const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
          /** @type {HTMLElement} */ (label).style.color = brightness > 128 ? '#000' : '#fff';
          const number = cell.querySelector('.fidget-grid__cell-number');
          if (number) /** @type {HTMLElement} */ (number).style.color = brightness > 128 ? '#000' : '#fff';
        }
      } else {
        /** @type {HTMLElement} */ (cell).style.backgroundColor = '#e0e0e0';
        const label = cell.querySelector('.fidget-grid__cell-label');
        if (label) {
          /** @type {HTMLElement} */ (label).textContent = '—';
          /** @type {HTMLElement} */ (label).style.color = '';
        }
      }
    });
  }
  
  /**
   * Render template grid
   */
  renderTemplateGrid() {
    const grid = /** @type {HTMLElement} */ (this.refs.templateGrid);
    if (!grid) return;
    
    grid.innerHTML = '';
    
    const templates = Object.entries(COLOR_TEMPLATES);
    
    templates.forEach(/** @param {[string, any]} entry */ (entry) => {
      const [key, template] = entry;
      if (template.category !== this.state.currentTemplateCategory) {
        return; // Skip templates from other categories
      }
      
      const card = document.createElement('div');
      card.className = 'template-card';
      card.dataset.templateKey = key;
      
      const icon = document.createElement('div');
      icon.className = 'template-card__icon';
      icon.textContent = template.icon;
      
      const name = document.createElement('div');
      name.className = 'template-card__name';
      name.textContent = template.name;
      
      const description = document.createElement('div');
      description.className = 'template-card__description';
      description.textContent = template.description;
      
      // Mini preview with product-specific layout
      const preview = document.createElement('div');
      preview.className = `template-card__preview template-card__preview--${this.productSize}`;
      const colors = template[this.productSize] || [];
      
      // Show all positions (not just 9) to match actual product layout
      colors.forEach(/** @param {string} coord */ (coord) => {
        const dot = document.createElement('div');
        dot.className = 'template-card__preview-dot';
        const color = /** @type {Record<string, any>} */ (KEYCAP_COLORS)[coord];
        if (color) {
          dot.style.backgroundColor = color.hex;
        }
        preview.appendChild(dot);
      });
      
      card.appendChild(icon);
      card.appendChild(name);
      card.appendChild(description);
      card.appendChild(preview);
      
      card.addEventListener('click', () => this.applyTemplate(key, template));
      
      grid.appendChild(card);
    });
  }
  
  /**
   * Filter templates by category
   * @param {any} event - The click event
   */
  filterTemplates(event) {
    const category = event.target.dataset.category;
    if (!category) return;
    
    this.state.currentTemplateCategory = category;
    
    // Update button states
    const buttons = this.querySelectorAll('.template-category-button');
    buttons.forEach(/** @param {Element} btn */ (btn) => {
      if (/** @type {HTMLElement} */ (btn).dataset.category === category) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });
    
    // Re-render templates
    this.renderTemplateGrid();
  }
  
  /**
   * Apply a color template
   * @param {string} key - The template key
   * @param {any} template - The template object
   */
  applyTemplate(key, template) {
    const colors = template[this.productSize];
    if (!colors) return;
    
    colors.forEach((/** @type {string} */ coordinate, /** @type {number} */ index) => {
      const position = this.layout.positions[index];
      const color = /** @type {Record<string, any>} */ (KEYCAP_COLORS)[coordinate];
      if (color && position) {
        this.state.keycaps[position] = { coordinate, ...color };
      }
    });
    
    this.state.selectedTemplate = key;
    
    // Update template card selection
    const cards = /** @type {HTMLElement} */ (this.refs.templateGrid).querySelectorAll('.template-card');
    cards.forEach(/** @param {Element} card */ (card) => {
      const htmlCard = /** @type {HTMLElement} */ (card);
      if (htmlCard.dataset.templateKey === key) {
        card.classList.add('selected');
      } else {
        card.classList.remove('selected');
      }
    });
    
    // Update grid display
    this.updateKeycapGridDisplay();
    this.updateSummary();
    this.updateFormInputs();
    this.updateAddToCartButton();
  }
  
  /**
   * Render all same color grid
   */
  renderAllSameColorGrid() {
    const grid = /** @type {HTMLElement} */ (this.refs.allSameColorGrid);
    if (!grid) return;
    
    const colors = Object.entries(KEYCAP_COLORS);
    const initialCount = 16;
    
    colors.forEach(/** @param {[string, any]} entry */ (entry, /** @param {number} index */ index) => {
      const [coordinate, color] = entry;
      const swatch = document.createElement('div');
      swatch.className = 'color-swatch';
      swatch.style.backgroundColor = color.hex;
      swatch.dataset.coordinate = coordinate;
      
      // Handle unavailable colors
      const isAvailable = color.available !== false;
      if (!isAvailable) {
        swatch.classList.add('color-swatch--unavailable');
        swatch.setAttribute('aria-disabled', 'true');
        swatch.style.cursor = 'not-allowed';
      }
      
      if (index >= initialCount) {
        swatch.style.display = 'none';
        swatch.dataset.hidden = 'true';
      }
      
      const label = document.createElement('div');
      label.className = 'color-swatch__label';
      label.textContent = `${coordinate} - ${color.name}`;
      swatch.appendChild(label);
      
      // Add out of stock badge
      if (!isAvailable) {
        const badge = document.createElement('div');
        badge.className = 'color-swatch__badge';
        badge.textContent = 'Out of Stock';
        swatch.appendChild(badge);
      }
      
      // Only allow clicking on available colors
      if (isAvailable) {
        swatch.addEventListener('click', () => this.applyAllSameColor(coordinate, color));
      }
      
      grid.appendChild(swatch);
    });
  }
  
  /**
   * Apply same color to all keycaps
   * @param {string} coordinate - The color coordinate
   * @param {any} color - The color object
   */
  applyAllSameColor(coordinate, color) {
    console.log('🎨 Applying all same color:', coordinate, color.name);
    
    this.layout.positions.forEach(/** @param {number} position */ (position) => {
      this.state.keycaps[position] = { coordinate, ...color };
    });
    
    this.state.allSameColor = /** @type {any} */ ({ coordinate, ...color });
    
    console.log('State after setting:', {
      allSameColor: this.state.allSameColor,
      designMode: this.state.designMode
    });
    
    // Update UI
    const swatches = /** @type {HTMLElement} */ (this.refs.allSameColorGrid).querySelectorAll('.color-swatch');
    swatches.forEach(/** @param {HTMLElement} s */ (s) => {
      if (s.dataset.coordinate === coordinate) {
        s.classList.add('selected');
      } else {
        s.classList.remove('selected');
      }
    });
    
    this.updateKeycapGridDisplay();
    this.updateSummary();
    this.updateFormInputs();
    this.updateAddToCartButton();
    
    // Verify the input was populated
    if (this.formInputs && this.formInputs.keycapConfig) {
      const value = this.formInputs.keycapConfig.value;
      console.log('Keycap config input value after update:', value);
    }
  }
  
  /**
   * Toggle showing all keycap colors
   */
  toggleAllKeycapColors() {
    this.state.showAllKeycapColors = !this.state.showAllKeycapColors;
    
    const grid = /** @type {HTMLElement} */ (this.refs.allSameColorGrid);
    const hiddenSwatches = grid.querySelectorAll('[data-hidden="true"]');
    
    if (this.state.showAllKeycapColors) {
      hiddenSwatches.forEach(/** @param {HTMLElement} s */ (s) => s.style.display = '');
      grid.classList.add('expanded');
      /** @type {HTMLElement} */ (this.refs.keycapColorToggleText).textContent = 'Show Less';
    } else {
      hiddenSwatches.forEach(/** @param {HTMLElement} s */ (s) => s.style.display = 'none');
      grid.classList.remove('expanded');
      /** @type {HTMLElement} */ (this.refs.keycapColorToggleText).textContent = 'Show All 45 Colors';
    }
  }
  
  /**
   * Render color palette for custom design
   */
  renderColorPalette() {
    const grid = /** @type {HTMLElement} */ (this.refs.colorPalette);
    if (!grid) return;
    
    this.updateColorPalette();
  }
  
  /**
   * Update color palette based on filter
   */
  updateColorPalette() {
    const grid = /** @type {HTMLElement} */ (this.refs.colorPalette);
    if (!grid) return;
    
    grid.innerHTML = '';
    
    const colors = Object.entries(KEYCAP_COLORS);
    
    colors.forEach(/** @param {[string, any]} entry */ (entry) => {
      const [coordinate, color] = entry;
      // Filter by family if needed
      if (this.state.currentPaletteFilter !== 'all') {
        // Support both single family (string) and multiple families (array)
        const families = Array.isArray(color.family) ? color.family : [color.family];
        if (!families.includes(this.state.currentPaletteFilter)) {
          return;
        }
      }
      
      const swatch = document.createElement('div');
      swatch.className = 'color-swatch';
      swatch.style.backgroundColor = color.hex;
      swatch.dataset.coordinate = coordinate;
      
      // Handle unavailable colors
      const isAvailable = color.available !== false;
      if (!isAvailable) {
        swatch.classList.add('color-swatch--unavailable');
        swatch.setAttribute('aria-disabled', 'true');
        swatch.style.cursor = 'not-allowed';
      }
      
      const label = document.createElement('div');
      label.className = 'color-swatch__label';
      label.textContent = `${coordinate}`;
      swatch.appendChild(label);
      
      // Add out of stock badge
      if (!isAvailable) {
        const badge = document.createElement('div');
        badge.className = 'color-swatch__badge';
        badge.textContent = 'Out';
        swatch.appendChild(badge);
      }
      
      // Only allow clicking on available colors
      if (isAvailable) {
        swatch.addEventListener('click', () => this.applyColorToKeycap(coordinate));
      }
      
      grid.appendChild(swatch);
    });
  }
  
  /**
   * Filter color palette
   * @param {any} event - The click event
   */
  filterPalette(event) {
    const family = event.target.dataset.family;
    if (!family) return;
    
    this.state.currentPaletteFilter = family;
    
    // Update button states
    const buttons = this.querySelectorAll('.palette-filter-button');
    buttons.forEach(/** @param {Element} btn */ (btn) => {
      if (/** @type {HTMLElement} */ (btn).dataset.family === family) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });
    
    // Update palette
    this.updateColorPalette();
  }
  
  /**
   * Handle design mode change
   */
  handleDesignModeChange() {
    const checkedRadio = /** @type {HTMLInputElement} */ (this.querySelector('input[name="design-mode"]:checked'));
    if (!checkedRadio) return;
    
    this.state.designMode = checkedRadio.value;
    
    // Hide all containers
    if (this.refs.templateContainer) /** @type {HTMLElement} */ (this.refs.templateContainer).style.display = 'none';
    if (this.refs.customDesignContainer) /** @type {HTMLElement} */ (this.refs.customDesignContainer).style.display = 'none';
    if (this.refs.allSameContainer) /** @type {HTMLElement} */ (this.refs.allSameContainer).style.display = 'none';
    if (this.refs.designerChoiceHint) /** @type {HTMLElement} */ (this.refs.designerChoiceHint).style.display = 'none';
    
    // Show relevant container
    switch (this.state.designMode) {
      case 'template':
        if (this.refs.templateContainer) /** @type {HTMLElement} */ (this.refs.templateContainer).style.display = 'block';
        break;
      case 'custom':
        if (this.refs.customDesignContainer) /** @type {HTMLElement} */ (this.refs.customDesignContainer).style.display = 'block';
        break;
      case 'all-same':
        if (this.refs.allSameContainer) /** @type {HTMLElement} */ (this.refs.allSameContainer).style.display = 'block';
        break;
      case 'designer-choice':
        // Clear previous keycap selections to avoid conflicts
        this.layout.positions.forEach(/** @param {number} pos */ (pos) => {
          this.state.keycaps[pos] = null;
        });
        this.state.allSameColor = null;
        this.state.selectedTemplate = null;
        
        // Update the visual grid to reflect cleared state
        this.updateKeycapGridDisplay();
        
        // Show hint
        if (this.refs.designerChoiceHint) /** @type {HTMLElement} */ (this.refs.designerChoiceHint).style.display = 'block';
        break;
    }
    
    this.updateSummary();
    this.updateFormInputs(); // Update form inputs when design mode changes
    this.updateAddToCartButton();
  }
  
  /**
   * Update summary display
   */
  updateSummary() {
    // Base color
    if (this.refs.summaryBaseColor) {
      const baseColor = /** @type {any} */ (this.state.baseColor);
      /** @type {HTMLElement} */ (this.refs.summaryBaseColor).textContent = 
        baseColor ? baseColor.name : '—';
    }
    
    // Switches
    if (this.refs.summarySwitches) {
      if (this.state.customSwitchMode || this.state.switchMode === 'custom') {
        // Count each switch type
        /** @type {Record<string, number>} */
        const counts = {};
        this.layout.positions.forEach(/** @param {number} pos */ (pos) => {
          const type = this.state.switches[pos];
          if (type) {
            counts[type] = (counts[type] || 0) + 1;
          }
        });
        
        const summary = Object.entries(counts)
          .map(/** @param {[string, number]} entry */ (entry) => {
            const [type, count] = entry;
            const switchData = /** @type {Record<string, any>} */ (SWITCH_TYPES)[type];
            return `<span class="fidget-summary__switch-item">${switchData?.icon || ''} ${count}× ${switchData?.name || type}</span>`;
          })
          .join(' ');
        
        /** @type {HTMLElement} */ (this.refs.summarySwitches).innerHTML = `Custom Mix: ${summary}`;
      } else {
        const type = this.state.switchMode.replace('all-', '');
        const switchData = /** @type {Record<string, any>} */ (SWITCH_TYPES)[type];
        /** @type {HTMLElement} */ (this.refs.summarySwitches).innerHTML = `${switchData?.icon || ''} All ${switchData?.name || ''}`;
      }
    }
    
    // Keycaps
    if (this.refs.summaryKeycaps) {
      let summary = '—';
      
      switch (this.state.designMode) {
        case 'template':
          if (this.state.selectedTemplate) {
            const template = /** @type {Record<string, any>} */ (COLOR_TEMPLATES)[this.state.selectedTemplate];
            summary = template ? template.name : '—';
          } else {
            summary = 'No template selected';
          }
          break;
        case 'custom':
          const filledCount = this.layout.positions.filter(/** @param {number} pos */ (pos) => this.state.keycaps[pos]).length;
          summary = `Custom Design (${filledCount}/${this.layout.positions.length} selected)`;
          break;
        case 'all-same':
          const allSameColor = /** @type {any} */ (this.state.allSameColor);
          summary = allSameColor 
            ? `All ${allSameColor.name}` 
            : 'No color selected';
          break;
        case 'designer-choice':
          summary = "Designer's Choice ✨";
          break;
      }
      
      /** @type {HTMLElement} */ (this.refs.summaryKeycaps).textContent = summary;
    }
    
    // Keycap summary text
    if (this.refs.keycapSummaryText) {
      let summaryText = 'No design selected';
      
      if (this.state.designMode === 'template' && this.state.selectedTemplate) {
        const template = /** @type {Record<string, any>} */ (COLOR_TEMPLATES)[this.state.selectedTemplate];
        summaryText = template ? `${template.name} applied` : 'Template not found';
      } else if (this.state.designMode === 'all-same' && this.state.allSameColor) {
        const color = /** @type {any} */ (this.state.allSameColor);
        summaryText = `All ${color.name} (${color.coordinate})`;
      } else if (this.state.designMode === 'custom') {
        const filledCount = this.layout.positions.filter(/** @param {number} pos */ (pos) => this.state.keycaps[pos]).length;
        summaryText = `${filledCount} of ${this.layout.positions.length} positions filled`;
      } else if (this.state.designMode === 'designer-choice') {
        summaryText = "Designer will choose colors";
      } else if (this.state.designMode === 'random-mix') {
        summaryText = "Random mix requested";
      }
      
      /** @type {HTMLElement} */ (this.refs.keycapSummaryText).textContent = summaryText;
    }
    
    // Update switch details
    this.updateSwitchDetails();
    
    // Update keycap details
    this.updateKeycapDetails();
    
    // Update visual preview
    this.updateVisualPreview();
  }
  
  /**
   * Update switch details list in summary
   */
  updateSwitchDetails() {
    if (!this.refs.summarySwitchDetails || !this.refs.summarySwitchList) return;
    
    const detailsContainer = /** @type {HTMLElement} */ (this.refs.summarySwitchDetails);
    const listContainer = /** @type {HTMLElement} */ (this.refs.summarySwitchList);
    
    // Clear existing list
    listContainer.innerHTML = '';
    
    // Always show switch details (since we always have switches assigned)
    detailsContainer.style.display = 'block';
    
    // Create list items for each position
    this.layout.positions.forEach(/** @param {number} position */ (position) => {
      const switchType = this.state.switches[position];
      
      if (switchType) {
        const switchData = /** @type {Record<string, any>} */ (SWITCH_TYPES)[switchType];
        
        const item = document.createElement('div');
        item.className = 'fidget-summary__switch-item-detail';
        
        // Switch icon
        const icon = document.createElement('div');
        icon.className = 'fidget-summary__switch-icon';
        icon.textContent = switchData?.icon || '⚙️';
        
        // Text
        const text = document.createElement('div');
        text.className = 'fidget-summary__switch-text';
        
        const positionSpan = document.createElement('div');
        positionSpan.className = 'fidget-summary__switch-position';
        positionSpan.textContent = `Pos ${position}:`;
        
        const typeSpan = document.createElement('div');
        typeSpan.className = 'fidget-summary__switch-type';
        typeSpan.textContent = switchData?.name || switchType;
        
        text.appendChild(positionSpan);
        text.appendChild(typeSpan);
        
        item.appendChild(icon);
        item.appendChild(text);
        
        listContainer.appendChild(item);
      }
    });
  }
  
  /**
   * Update keycap details list in summary
   */
  updateKeycapDetails() {
    if (!this.refs.summaryKeycapDetails || !this.refs.summaryKeycapList) return;
    
    const detailsContainer = /** @type {HTMLElement} */ (this.refs.summaryKeycapDetails);
    const listContainer = /** @type {HTMLElement} */ (this.refs.summaryKeycapList);
    
    // Clear existing list
    listContainer.innerHTML = '';
    
    // Determine if we should show details
    let shouldShowDetails = false;
    
    switch (this.state.designMode) {
      case 'template':
      case 'custom':
      case 'all-same':
        // Show details if we have keycaps assigned
        shouldShowDetails = this.layout.positions.some(/** @param {number} pos */ (pos) => this.state.keycaps[pos]);
        break;
      case 'designer-choice':
        // Don't show details for designer's choice
        shouldShowDetails = false;
        break;
    }
    
    if (!shouldShowDetails) {
      detailsContainer.style.display = 'none';
      return;
    }
    
    // Show details
    detailsContainer.style.display = 'block';
    
    // Create list items for each position
    this.layout.positions.forEach(/** @param {number} position */ (position) => {
      const keycap = this.state.keycaps[position];
      
      if (keycap) {
        const item = document.createElement('div');
        item.className = 'fidget-summary__keycap-item';
        
        // Color dot
        const dot = document.createElement('div');
        dot.className = 'fidget-summary__keycap-dot';
        dot.style.backgroundColor = keycap.hex;
        
        // Text
        const text = document.createElement('div');
        text.className = 'fidget-summary__keycap-text';
        
        const positionSpan = document.createElement('div');
        positionSpan.className = 'fidget-summary__keycap-position';
        positionSpan.textContent = `Pos ${position}:`;
        
        const colorSpan = document.createElement('div');
        colorSpan.className = 'fidget-summary__keycap-color';
        colorSpan.textContent = `${keycap.coordinate} - ${keycap.name}`;
        
        text.appendChild(positionSpan);
        text.appendChild(colorSpan);
        
        item.appendChild(dot);
        item.appendChild(text);
        
        listContainer.appendChild(item);
      }
    });
  }
  
  /**
   * Update visual preview in summary
   */
  updateVisualPreview() {
    if (!this.refs.summaryPreviewGrid || !this.refs.summaryPreviewFrame) return;
    
    const grid = /** @type {HTMLElement} */ (this.refs.summaryPreviewGrid);
    const frame = /** @type {HTMLElement} */ (this.refs.summaryPreviewFrame);
    
    // Set grid layout class
    grid.className = `fidget-summary__preview-grid grid-${this.productSize}`;
    
    // Update frame color (base color)
    if (this.state.baseColor) {
      const baseColor = /** @type {any} */ (this.state.baseColor);
      frame.style.backgroundColor = baseColor.hex;
    } else {
      frame.style.backgroundColor = '#e0e0e0';
    }
    
    // Clear existing cells
    grid.innerHTML = '';
    
    // Add cells for each position
    this.layout.positions.forEach(/** @param {number} position */ (position) => {
      const cell = document.createElement('div');
      cell.className = 'fidget-summary__preview-cell';
      
      // Get keycap color for this position
      const keycap = this.state.keycaps[position];
      if (keycap) {
        cell.style.backgroundColor = keycap.hex;
      } else {
        cell.style.backgroundColor = '#f5f5f5';
      }
      
      grid.appendChild(cell);
    });
  }
  
  /**
   * Update hidden form inputs
   */
  updateFormInputs() {
    if (!this.formInputs) {
      console.warn('⚠️ Form inputs not initialized yet');
      return;
    }
    
    // Update special instructions when textarea changes
    if (this.refs.specialInstructions) {
      this.state.specialInstructions = /** @type {HTMLInputElement | HTMLTextAreaElement} */ (this.refs.specialInstructions).value;
    }
    
    // Base color
    if (this.formInputs.baseColor) {
      const baseColor = /** @type {any} */ (this.state.baseColor);
      this.formInputs.baseColor.value = baseColor 
        ? baseColor.name 
        : '';
    }
    
    // Switch configuration
    if (this.formInputs.switchConfig) {
      let switchConfig = '';
      
      if (this.state.customSwitchMode || this.state.switchMode === 'custom') {
        // Group positions by switch type
        /** @type {Record<string, number[]>} */
        const groups = {};
        this.layout.positions.forEach(/** @param {number} pos */ (pos) => {
          const type = this.state.switches[pos];
          if (!type) return;
          if (!groups[type]) groups[type] = [];
          groups[type].push(pos);
        });
        
        switchConfig = Object.entries(groups)
          .map(/** @param {[string, number[]]} entry */ (entry) => {
            const [type, positions] = entry;
            const switchData = /** @type {Record<string, any>} */ (SWITCH_TYPES)[type];
            return `${switchData?.name || type}(${positions.join(',')})`;
          })
          .join(', ');
        
        switchConfig = `Custom Mix: ${switchConfig}`;
      } else {
        const type = this.state.switchMode.replace('all-', '');
        const switchData = /** @type {Record<string, any>} */ (SWITCH_TYPES)[type];
        switchConfig = `All ${switchData?.name || ''}`;
      }
      
      this.formInputs.switchConfig.value = switchConfig;
    }
    
    // Keycap configuration
    if (this.formInputs.keycapConfig) {
      let keycapConfig = '';
      
      switch (this.state.designMode) {
        case 'template':
          if (this.state.selectedTemplate) {
            const template = /** @type {Record<string, any>} */ (COLOR_TEMPLATES)[this.state.selectedTemplate];
            const coords = this.layout.positions
              .map(/** @param {number} pos */ (pos) => this.state.keycaps[pos]?.coordinate)
              .filter(Boolean)
              .join(', ');
            keycapConfig = `Template: ${template.name} (${coords})`;
          }
          break;
        case 'custom':
          const customCoords = this.layout.positions
            .map((/** @type {number} */ pos, /** @type {number} */ index) => {
              const keycap = this.state.keycaps[pos];
              return keycap ? `${pos}:${keycap.coordinate}` : null;
            })
            .filter(Boolean)
            .join(', ');
          keycapConfig = `Custom: ${customCoords}`;
          break;
        case 'all-same':
          // Try multiple sources for the all-same color
          let allSameColor = this.state.allSameColor;
          
          // Fallback 1: Check if all keycaps are set to the same color
          if (!allSameColor && this.state.keycaps[this.layout.positions[0]]) {
            const firstKeycap = this.state.keycaps[this.layout.positions[0]];
            // Verify all positions have the same color
            const allSame = this.layout.positions.every(/** @param {number} pos */ (pos) => {
              const keycap = this.state.keycaps[pos];
              return keycap && keycap.coordinate === firstKeycap.coordinate;
            });
            
            if (allSame) {
              allSameColor = firstKeycap;
            }
          }
          
          if (allSameColor) {
            keycapConfig = `All ${allSameColor.coordinate} (${allSameColor.name})`;
          } else {
            console.error('All-same mode but no color set. State:', {
              allSameColor: this.state.allSameColor,
              keycaps: this.state.keycaps,
              designMode: this.state.designMode
            });
          }
          break;
        case 'designer-choice':
          keycapConfig = "Designer's Choice";
          if (this.state.specialInstructions) {
            keycapConfig += ` - See special instructions`;
          }
          break;
      }
      
      // Ensure keycap config is never empty
      if (!keycapConfig || keycapConfig.trim() === '') {
        console.error('Keycap configuration is empty, state:', this.state.designMode, this.state.allSameColor);
        keycapConfig = `${this.state.designMode} - Please contact us if you see this message`;
      }
      
      this.formInputs.keycapConfig.value = keycapConfig;
    }
    
    // Special instructions
    if (this.formInputs.specialInstructions) {
      this.formInputs.specialInstructions.value = this.state.specialInstructions;
    }
    
    // JSON config for order processing
    if (this.formInputs.configJSON) {
      const config = {
        productSize: this.productSize,
        baseColor: this.state.baseColor,
        switches: this.state.switches,
        keycaps: this.state.keycaps,
        designMode: this.state.designMode,
        selectedTemplate: this.state.selectedTemplate,
        switchMode: this.state.switchMode,
        specialInstructions: this.state.specialInstructions
      };
      
      this.formInputs.configJSON.value = JSON.stringify(config);
    }
    
    // Note: Unique order ID is now generated on form submit, not here
  }
  
  /**
   * Helper: Convert hex to RGB
   * @param {string} hex - The hex color string
   * @returns {{r: number, g: number, b: number}} RGB object
   */
  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result || !result[1] || !result[2] || !result[3]) {
      return { r: 0, g: 0, b: 0 };
    }
    return {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    };
  }
}

// Register component
customElements.define('fidget-customizer-component', FidgetCustomizer);

export default FidgetCustomizer;

